import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderKanban, ChevronRight, Calendar, HardHat, Search,
  FileText, Image as ImageIcon, FileSpreadsheet, FileArchive,
  File as FileIcon, Download, Briefcase, MessageSquare,
} from 'lucide-react';
import {
  clients, utilisateurClient, typesProjet,
  formatDate, formatDateTime,
} from '../data/mockData';

const docIcon = {
  pdf: FileText,
  image: ImageIcon,
  doc: FileText,
  xls: FileSpreadsheet,
  zip: FileArchive,
};

const docBadge = {
  pdf: 'danger',
  image: 'info',
  doc: 'sky',
  xls: 'ok',
  zip: 'warn',
};

const docLabel = {
  pdf: 'PDF',
  image: 'Image',
  doc: 'DOC',
  xls: 'XLSX',
  zip: 'ZIP',
};

const statutBadge = (statut) => {
  if (statut === 'livre') return 'ok';
  if (statut === 'etude') return 'warn';
  return 'info';
};

const statutText = (statut) => {
  if (statut === 'livre') return 'Livre';
  if (statut === 'etude') return 'En etude';
  return 'En cours';
};

const EspaceClient = ({ defaultTab = 'projets' }) => {
  const navigate = useNavigate();
  const client = clients.find((c) => c.id === utilisateurClient.clientId);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [search, setSearch] = useState('');

  if (!client) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <Briefcase size={28} />
          <p>Aucun client rattache a ce compte.</p>
        </div>
      </div>
    );
  }

  const projets = client.chantiers;
  const tousDocuments = projets.flatMap((p) =>
    (p.documentsClient || []).map((d) => ({
      ...d,
      projetId: p.id,
      projetNom: p.nom,
    }))
  );

  const projetsFiltres = projets.filter((p) =>
    p.nom.toLowerCase().includes(search.toLowerCase()) ||
    p.id.toLowerCase().includes(search.toLowerCase())
  );

  const documentsFiltres = tousDocuments.filter((d) =>
    d.nom.toLowerCase().includes(search.toLowerCase()) ||
    d.projetNom.toLowerCase().includes(search.toLowerCase()) ||
    d.categorie.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title-row">
            <div className="page-icon-wrapper">
              <Briefcase size={22} />
            </div>
            <div>
              <h1 className="page-title">Bonjour {utilisateurClient.prenom}</h1>
              <p className="page-subtitle">
                Espace dedie <strong>{client.nom}</strong> - Suivi de vos projets et documents partages
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card">
          <div className="kpi-icon"><FolderKanban size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Projets actifs</span>
            <span className="kpi-value">
              {projets.filter((p) => p.statut !== 'livre').length}
            </span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><HardHat size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Projets livres</span>
            <span className="kpi-value">
              {projets.filter((p) => p.statut === 'livre').length}
            </span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><FileText size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Documents partages</span>
            <span className="kpi-value">{tousDocuments.length}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><MessageSquare size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Notes d info actives</span>
            <span className="kpi-value">
              {projets.filter((p) => p.infosClient && p.infosClient.trim().length > 0).length}
            </span>
          </div>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: '1.25rem' }}>
        <button
          className={`tab ${activeTab === 'projets' ? 'active' : ''}`}
          onClick={() => setActiveTab('projets')}
        >
          <FolderKanban size={14} /> Mes projets ({projets.length})
        </button>
        <button
          className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <FileText size={14} /> Tous les documents ({tousDocuments.length})
        </button>
      </div>

      <div className="toolbar" style={{ marginTop: 0 }}>
        <div className="search-input grow">
          <Search size={16} />
          <input
            placeholder={activeTab === 'projets'
              ? 'Rechercher un projet'
              : 'Rechercher un document, un projet ou une categorie'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {activeTab === 'projets' && (
        <div className="projets-grid">
          {projetsFiltres.length === 0 ? (
            <div className="empty-state">
              <FolderKanban size={28} />
              <p>Aucun projet ne correspond a votre recherche.</p>
            </div>
          ) : (
            projetsFiltres.map((p) => {
              const tp = typesProjet.find((t) => t.id === p.typeProjet);
              const nbDocs = (p.documentsClient || []).length;
              const aInfos = p.infosClient && p.infosClient.trim().length > 0;
              return (
                <button
                  key={p.id}
                  className="projet-card"
                  onClick={() => navigate(`/espace-client/projets/${p.id}`)}
                >
                  <div className="projet-card-head">
                    <div className="projet-card-icon">
                      <HardHat size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong>{p.nom}</strong>
                      <span className="meta-line mono">{p.id}</span>
                    </div>
                    <ChevronRight size={16} className="arrow-muted" />
                  </div>

                  <div className="projet-card-badges">
                    {tp && <span className={`badge badge-${tp.badge}`}>{tp.label}</span>}
                    <span className={`badge badge-${statutBadge(p.statut)}`}>
                      {statutText(p.statut)}
                    </span>
                  </div>

                  <div className="projet-card-progress">
                    <div className="progress">
                      <div className="progress-bar" style={{ width: `${p.avancement}%` }} />
                    </div>
                    <span className="mono">{p.avancement}%</span>
                  </div>

                  <div className="projet-card-foot">
                    <span className="meta-line">
                      <Calendar size={11} /> Fin prevue {formatDate(p.dateFinPrevue)}
                    </span>
                    <span className="meta-line">
                      <FileText size={11} /> {nbDocs} doc.
                    </span>
                    {aInfos && (
                      <span className="meta-line" title="Note d information disponible">
                        <MessageSquare size={11} /> Note
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="card">
          <div className="card-body">
            {documentsFiltres.length === 0 ? (
              <div className="empty-state compact">
                <FileText size={22} />
                <p>Aucun document partage pour le moment.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Categorie</th>
                    <th>Projet</th>
                    <th>Date de depot</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {documentsFiltres.map((d) => {
                    const Icon = docIcon[d.type] || FileIcon;
                    const badge = docBadge[d.type] || 'neutral';
                    return (
                      <tr key={d.id} className="hoverable">
                        <td>
                          <div className="cell-strong">
                            <Icon size={16} className="cell-icon" />
                            <span className="mono" style={{ fontSize: '0.82rem' }}>{d.nom}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`badge badge-${badge}`}>{docLabel[d.type] || 'Fichier'}</span>
                        </td>
                        <td>
                          <span className="chip chip-sky">{d.categorie}</span>
                        </td>
                        <td>
                          <button
                            className="btn-link"
                            onClick={() => navigate(`/espace-client/projets/${d.projetId}`)}
                          >
                            {d.projetNom}
                          </button>
                        </td>
                        <td>{formatDateTime(d.dateAjout)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn-ghost btn-small">
                            <Download size={14} /> Telecharger
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EspaceClient;
